// src/redux/groups/groupSlice.js
import { createSlice } from '@reduxjs/toolkit';
import {
  fetchGroups, fetchGroupById, fetchGroupByName,
  createGroup, updateGroup, deleteGroup,
  addMemberToGroup, removeMemberFromGroup, fetchGroupMembers,
  joinGroup, leaveGroup, approveMember, rejectMember,
  fetchMyGroupInvites, acceptGroupInvite, declineGroupInvite,
  updateMemberRole, transferOwnership, muteMember, unmuteMember, updateGroupBackgroundTheme,
  createGroupMessage, fetchGroupMessages, updateGroupMessage, deleteGroupMessage,
  upsertGroupDisplayPicture, fetchGroupDisplayPicture, deleteGroupDisplayPicture,
  searchUsers, searchGroupsByStock, getGroupAnalytics
} from './userGroupies';

const initialState = {
  groups: [],
  currentGroup: null,
  members: [],
  messages: [],
  displayPicture: null,
  searchResults: [],
  analytics: null,

  // Global list/status
  status: 'idle',
  error: null,

  // Group CRUD
  createGroupStatus: 'idle',
  createGroupError: null,
  updateGroupStatus: 'idle',
  updateGroupError: null,
  deleteGroupStatus: 'idle',
  deleteGroupError: null,

  // Membership operations
  addMemberStatus: 'idle',
  addMemberError: null,
  removeMemberStatus: 'idle',
  removeMemberError: null,
  fetchMembersStatus: 'idle',
  fetchMembersError: null,
  joinGroupStatus: 'idle',
  joinGroupError: null,
  leaveGroupStatus: 'idle',
  leaveGroupError: null,
  approveMemberStatus: 'idle',
  approveMemberError: null,
  rejectMemberStatus: 'idle',
  rejectMemberError: null,

  // My pending invites (invitee-side accept/decline)
  myInvites: [],
  fetchMyInvitesStatus: 'idle',
  fetchMyInvitesError: null,
  acceptInviteStatus: 'idle',
  acceptInviteError: null,
  declineInviteStatus: 'idle',
  declineInviteError: null,
  updateRoleStatus: 'idle',
  updateRoleError: null,
  transferOwnershipStatus: 'idle',
  transferOwnershipError: null,
  muteStatus: 'idle',
  muteError: null,

  // Message operations
  createMessageStatus: 'idle',
  createMessageError: null,
  fetchMessagesStatus: 'idle',
  fetchMessagesError: null,
  updateMessageStatus: 'idle',
  updateMessageError: null,
  deleteMessageStatus: 'idle',
  deleteMessageError: null,

  // Theme operations. Was missing entirely -- GroupHeader.jsx already had a
  // fully-wired `saving` prop, but nothing ever set these, so the picker's
  // "disable while saving" behavior never actually triggered and a failed
  // theme change produced zero visible feedback.
  updateThemeStatus: 'idle',
  updateThemeError: null,

  // Picture operations
  upsertPictureStatus: 'idle',
  upsertPictureError: null,
  fetchPictureStatus: 'idle',
  fetchPictureError: null,
  deletePictureStatus: 'idle',
  deletePictureError: null,

  // Search & analytics
  searchStatus: 'idle',
  searchError: null,
  analyticsStatus: 'idle',
  analyticsError: null,
};

const groupSlice = createSlice({
  name: 'groups',
  initialState,
  reducers: {
    clearCurrentGroup: (state) => {
      state.currentGroup = null;
      state.members = [];
      state.messages = [];
      state.displayPicture = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
    resetAllErrors: (state) => {
      state.error = null;

      state.createGroupError = null;
      state.updateGroupError = null;
      state.deleteGroupError = null;

      state.addMemberError = null;
      state.removeMemberError = null;
      state.fetchMembersError = null;
      state.joinGroupError = null;
      state.leaveGroupError = null;
      state.approveMemberError = null;
      state.rejectMemberError = null;
      state.fetchMyInvitesError = null;
      state.acceptInviteError = null;
      state.declineInviteError = null;
      state.updateRoleError = null;
      state.transferOwnershipError = null;
      state.muteError = null;

      state.createMessageError = null;
      state.fetchMessagesError = null;
      state.updateMessageError = null;
      state.deleteMessageError = null;

      state.upsertPictureError = null;
      state.fetchPictureError = null;
      state.deletePictureError = null;

      state.searchError = null;
      state.analyticsError = null;
    },
  },
  extraReducers: (builder) => {
    // ===== GROUP LIST & GET =====
    builder
      .addCase(fetchGroups.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const payload = action.payload;
        state.groups = Array.isArray(payload?.groups) ? payload.groups : (Array.isArray(payload) ? payload : []);
      })
      .addCase(fetchGroups.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error?.message || 'Failed to fetch groups';
      });

    builder
      .addCase(fetchGroupById.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchGroupById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentGroup = action.payload || null;
        // keep members/messages/displayPicture as-is; they have their own fetches
      })
      .addCase(fetchGroupById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error?.message || 'Failed to fetch group';
      });

    builder
      .addCase(fetchGroupByName.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchGroupByName.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentGroup = action.payload || null;
      })
      .addCase(fetchGroupByName.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error?.message || 'Failed to fetch group by name';
      });

    // ===== GROUP CRUD =====
    builder
      .addCase(createGroup.pending, (state) => {
        state.createGroupStatus = 'loading';
        state.createGroupError = null;
      })
      .addCase(createGroup.fulfilled, (state, action) => {
        state.createGroupStatus = 'succeeded';
        const g = action.payload;
        if (g) {
          state.groups.unshift(g);
          state.currentGroup = g;
        }
      })
      .addCase(createGroup.rejected, (state, action) => {
        state.createGroupStatus = 'failed';
        state.createGroupError = action.payload || action.error?.message || 'Failed to create group';
      });

    builder
      .addCase(updateGroup.pending, (state) => {
        state.updateGroupStatus = 'loading';
        state.updateGroupError = null;
      })
      .addCase(updateGroup.fulfilled, (state, action) => {
        state.updateGroupStatus = 'succeeded';
        const updated = action.payload;
        if (!updated) return;
        state.groups = state.groups.map(g => (g.id === updated.id ? { ...g, ...updated } : g));
        if (state.currentGroup?.id === updated.id) {
          state.currentGroup = { ...state.currentGroup, ...updated };
        }
      })
      .addCase(updateGroupBackgroundTheme.pending, (state) => {
        state.updateThemeStatus = 'loading';
        state.updateThemeError = null;
      })
      .addCase(updateGroupBackgroundTheme.fulfilled, (state, action) => {
        state.updateThemeStatus = 'succeeded';
        const updated = action.payload;
        if (!updated) return;
        state.groups = state.groups.map(g => (g.id === updated.id ? { ...g, ...updated } : g));
        if (state.currentGroup?.id === updated.id) {
          state.currentGroup = { ...state.currentGroup, ...updated };
        }
      })
      .addCase(updateGroupBackgroundTheme.rejected, (state, action) => {
        state.updateThemeStatus = 'failed';
        state.updateThemeError = action.payload || action.error?.message || 'Failed to update background theme';
      })
      .addCase(updateGroup.rejected, (state, action) => {
        state.updateGroupStatus = 'failed';
        state.updateGroupError = action.payload || action.error?.message || 'Failed to update group';
      });

    builder
      .addCase(deleteGroup.pending, (state) => {
        state.deleteGroupStatus = 'loading';
        state.deleteGroupError = null;
      })
      .addCase(deleteGroup.fulfilled, (state, action) => {
        state.deleteGroupStatus = 'succeeded';
        const deletedId = action.meta?.arg ?? action.payload?.id ?? action.payload;
        if (deletedId != null) {
          state.groups = state.groups.filter(g => g.id !== deletedId);
          if (state.currentGroup?.id === deletedId) {
            state.currentGroup = null;
            state.members = [];
            state.messages = [];
            state.displayPicture = null;
          }
        }
      })
      .addCase(deleteGroup.rejected, (state, action) => {
        state.deleteGroupStatus = 'failed';
        state.deleteGroupError = action.payload || action.error?.message || 'Failed to delete group';
      });

    // ===== MEMBERS =====
    builder
      .addCase(fetchGroupMembers.pending, (state) => {
        state.fetchMembersStatus = 'loading';
        state.fetchMembersError = null;
      })
      .addCase(fetchGroupMembers.fulfilled, (state, action) => {
        state.fetchMembersStatus = 'succeeded';
        const payload = action.payload;
        state.members = Array.isArray(payload?.members) ? payload.members : (Array.isArray(payload) ? payload : []);
      })
      .addCase(fetchGroupMembers.rejected, (state, action) => {
        state.fetchMembersStatus = 'failed';
        state.fetchMembersError = action.payload || action.error?.message || 'Failed to fetch members';
      });

    builder
      .addCase(addMemberToGroup.pending, (state) => {
        state.addMemberStatus = 'loading';
        state.addMemberError = null;
      })
      .addCase(addMemberToGroup.fulfilled, (state, action) => {
        state.addMemberStatus = 'succeeded';
        const payload = action.payload;
        if (Array.isArray(payload?.members)) {
          state.members = payload.members;
        } else if (payload && (payload.id || payload.user_id)) {
          const id = payload.id ?? payload.user_id;
          const exists = state.members.some(m => (m.id ?? m.user_id) === id);
          if (!exists) state.members.push(payload);
        }
      })
      .addCase(addMemberToGroup.rejected, (state, action) => {
        state.addMemberStatus = 'failed';
        state.addMemberError = action.payload || action.error?.message || 'Failed to add member';
      });

    builder
      .addCase(removeMemberFromGroup.pending, (state) => {
        state.removeMemberStatus = 'loading';
        state.removeMemberError = null;
      })
      .addCase(removeMemberFromGroup.fulfilled, (state, action) => {
        state.removeMemberStatus = 'succeeded';
        const payload = action.payload;
        const removedId =
          action.meta?.arg?.userId ??
          payload?.removedUserId ??
          payload?.user_id ??
          payload?.id ??
          payload;
        if (removedId != null) {
          state.members = state.members.filter(m => (m.id ?? m.user_id) !== removedId);
        }
      })
      .addCase(removeMemberFromGroup.rejected, (state, action) => {
        state.removeMemberStatus = 'failed';
        state.removeMemberError = action.payload || action.error?.message || 'Failed to remove member';
      });

    builder
      .addCase(joinGroup.pending, (state) => {
        state.joinGroupStatus = 'loading';
        state.joinGroupError = null;
      })
      .addCase(joinGroup.fulfilled, (state, action) => {
        state.joinGroupStatus = 'succeeded';
        // Optional: reflect membership on currentGroup if backend returns it
        if (state.currentGroup && typeof state.currentGroup.is_member !== 'undefined') {
          state.currentGroup.is_member = true;
        }
      })
      .addCase(joinGroup.rejected, (state, action) => {
        state.joinGroupStatus = 'failed';
        state.joinGroupError = action.payload || action.error?.message || 'Failed to join group';
      });

    builder
      .addCase(leaveGroup.pending, (state) => {
        state.leaveGroupStatus = 'loading';
        state.leaveGroupError = null;
      })
      .addCase(leaveGroup.fulfilled, (state) => {
        state.leaveGroupStatus = 'succeeded';
        if (state.currentGroup && typeof state.currentGroup.is_member !== 'undefined') {
          state.currentGroup.is_member = false;
        }
      })
      .addCase(leaveGroup.rejected, (state, action) => {
        state.leaveGroupStatus = 'failed';
        state.leaveGroupError = action.payload || action.error?.message || 'Failed to leave group';
      });

    builder
      .addCase(approveMember.pending, (state) => {
        state.approveMemberStatus = 'loading';
        state.approveMemberError = null;
      })
      .addCase(approveMember.fulfilled, (state, action) => {
        state.approveMemberStatus = 'succeeded';
        const member = action.payload;
        if (!member) return;
        state.members = state.members.map(m => (m.id === member.id || m.user_id === member.user_id ? { ...m, ...member } : m));
      })
      .addCase(approveMember.rejected, (state, action) => {
        state.approveMemberStatus = 'failed';
        state.approveMemberError = action.payload || action.error?.message || 'Failed to approve member';
      });

    builder
      .addCase(rejectMember.pending, (state) => {
        state.rejectMemberStatus = 'loading';
        state.rejectMemberError = null;
      })
      .addCase(rejectMember.fulfilled, (state, action) => {
        state.rejectMemberStatus = 'succeeded';
        const payload = action.payload;
        const rejectedId = payload?.user_id ?? payload?.id ?? payload;
        if (rejectedId != null) {
          state.members = state.members.filter(m => (m.id ?? m.user_id) !== rejectedId);
        }
      })
      .addCase(rejectMember.rejected, (state, action) => {
        state.rejectMemberStatus = 'failed';
        state.rejectMemberError = action.payload || action.error?.message || 'Failed to reject member';
      });

    builder
      .addCase(fetchMyGroupInvites.pending, (state) => {
        state.fetchMyInvitesStatus = 'loading';
        state.fetchMyInvitesError = null;
      })
      .addCase(fetchMyGroupInvites.fulfilled, (state, action) => {
        state.fetchMyInvitesStatus = 'succeeded';
        state.myInvites = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchMyGroupInvites.rejected, (state, action) => {
        state.fetchMyInvitesStatus = 'failed';
        state.fetchMyInvitesError = action.payload || action.error?.message || 'Failed to fetch pending invites';
      });

    builder
      .addCase(acceptGroupInvite.pending, (state) => {
        state.acceptInviteStatus = 'loading';
        state.acceptInviteError = null;
      })
      .addCase(acceptGroupInvite.fulfilled, (state, action) => {
        state.acceptInviteStatus = 'succeeded';
        const { groupId } = action.payload;
        state.myInvites = state.myInvites.filter((inv) => inv.group_id !== groupId);
      })
      .addCase(acceptGroupInvite.rejected, (state, action) => {
        state.acceptInviteStatus = 'failed';
        state.acceptInviteError = action.payload || action.error?.message || 'Failed to accept invite';
      });

    builder
      .addCase(declineGroupInvite.pending, (state) => {
        state.declineInviteStatus = 'loading';
        state.declineInviteError = null;
      })
      .addCase(declineGroupInvite.fulfilled, (state, action) => {
        state.declineInviteStatus = 'succeeded';
        const { groupId } = action.payload;
        state.myInvites = state.myInvites.filter((inv) => inv.group_id !== groupId);
      })
      .addCase(declineGroupInvite.rejected, (state, action) => {
        state.declineInviteStatus = 'failed';
        state.declineInviteError = action.payload || action.error?.message || 'Failed to decline invite';
      });

    builder
      .addCase(updateMemberRole.pending, (state) => {
        state.updateRoleStatus = 'loading';
        state.updateRoleError = null;
      })
      .addCase(updateMemberRole.fulfilled, (state, action) => {
        state.updateRoleStatus = 'succeeded';
        const updated = action.payload;
        if (!updated) return;
        state.members = state.members.map(m =>
          (m.id === updated.id || m.user_id === updated.user_id) ? { ...m, ...updated } : m
        );
      })
      .addCase(updateMemberRole.rejected, (state, action) => {
        state.updateRoleStatus = 'failed';
        state.updateRoleError = action.payload || action.error?.message || 'Failed to update member role';
      });

    builder
      .addCase(transferOwnership.pending, (state) => {
        state.transferOwnershipStatus = 'loading';
        state.transferOwnershipError = null;
      })
      .addCase(transferOwnership.fulfilled, (state, action) => {
        state.transferOwnershipStatus = 'succeeded';
        // If backend returns new owner, reflect it
        const payload = action.payload;
        if (state.currentGroup && payload) {
          const newOwnerId = payload?.newOwnerUserId ?? payload?.owner_user_id;
          if (newOwnerId) {
            state.currentGroup.owner_user_id = newOwnerId;
          }
        }
      })
      .addCase(transferOwnership.rejected, (state, action) => {
        state.transferOwnershipStatus = 'failed';
        state.transferOwnershipError = action.payload || action.error?.message || 'Failed to transfer ownership';
      });

    builder
      .addCase(muteMember.pending, (state) => {
        state.muteStatus = 'loading';
        state.muteError = null;
      })
      .addCase(muteMember.fulfilled, (state, action) => {
        state.muteStatus = 'succeeded';
        const updated = action.payload;
        if (!updated) return;
        state.members = state.members.map(m =>
          (m.id === updated.id || m.user_id === updated.user_id) ? { ...m, ...updated } : m
        );
      })
      .addCase(muteMember.rejected, (state, action) => {
        state.muteStatus = 'failed';
        state.muteError = action.payload || action.error?.message || 'Failed to mute member';
      });

    builder
      .addCase(unmuteMember.pending, (state) => {
        state.muteStatus = 'loading';
        state.muteError = null;
      })
      .addCase(unmuteMember.fulfilled, (state, action) => {
        state.muteStatus = 'succeeded';
        const updated = action.payload;
        if (!updated) return;
        state.members = state.members.map(m =>
          (m.id === updated.id || m.user_id === updated.user_id) ? { ...m, ...updated } : m
        );
      })
      .addCase(unmuteMember.rejected, (state, action) => {
        state.muteStatus = 'failed';
        state.muteError = action.payload || action.error?.message || 'Failed to unmute member';
      });

    // ===== MESSAGES =====
    builder
      .addCase(fetchGroupMessages.pending, (state) => {
        state.fetchMessagesStatus = 'loading';
        state.fetchMessagesError = null;
      })
      .addCase(fetchGroupMessages.fulfilled, (state, action) => {
        state.fetchMessagesStatus = 'succeeded';
        const payload = action.payload;
        state.messages = Array.isArray(payload?.messages) ? payload.messages : (Array.isArray(payload) ? payload : []);
      })
      .addCase(fetchGroupMessages.rejected, (state, action) => {
        state.fetchMessagesStatus = 'failed';
        state.fetchMessagesError = action.payload || action.error?.message || 'Failed to fetch messages';
      });

    builder
      .addCase(createGroupMessage.pending, (state) => {
        state.createMessageStatus = 'loading';
        state.createMessageError = null;
      })
      .addCase(createGroupMessage.fulfilled, (state, action) => {
        state.createMessageStatus = 'succeeded';
        const msg = action.payload;
        if (msg) state.messages.unshift(msg);
      })
      .addCase(createGroupMessage.rejected, (state, action) => {
        state.createMessageStatus = 'failed';
        state.createMessageError = action.payload || action.error?.message || 'Failed to create message';
      });

    builder
      .addCase(updateGroupMessage.pending, (state) => {
        state.updateMessageStatus = 'loading';
        state.updateMessageError = null;
      })
      .addCase(updateGroupMessage.fulfilled, (state, action) => {
        state.updateMessageStatus = 'succeeded';
        const updated = action.payload;
        if (!updated) return;
        state.messages = state.messages.map(m => (m.id === updated.id ? { ...m, ...updated } : m));
      })
      .addCase(updateGroupMessage.rejected, (state, action) => {
        state.updateMessageStatus = 'failed';
        state.updateMessageError = action.payload || action.error?.message || 'Failed to update message';
      });

    builder
      .addCase(deleteGroupMessage.pending, (state) => {
        state.deleteMessageStatus = 'loading';
        state.deleteMessageError = null;
      })
      .addCase(deleteGroupMessage.fulfilled, (state, action) => {
        state.deleteMessageStatus = 'succeeded';
        const deletedId = action.meta?.arg?.messageId ?? action.payload?.id ?? action.payload;
        if (deletedId != null) {
          state.messages = state.messages.filter(m => m.id !== deletedId);
        }
      })
      .addCase(deleteGroupMessage.rejected, (state, action) => {
        state.deleteMessageStatus = 'failed';
        state.deleteMessageError = action.payload || action.error?.message || 'Failed to delete message';
      });

    // ===== DISPLAY PICTURE =====
    builder
      .addCase(fetchGroupDisplayPicture.pending, (state) => {
        state.fetchPictureStatus = 'loading';
        state.fetchPictureError = null;
      })
      .addCase(fetchGroupDisplayPicture.fulfilled, (state, action) => {
        state.fetchPictureStatus = 'succeeded';
        state.displayPicture = action.payload ?? null;
      })
      .addCase(fetchGroupDisplayPicture.rejected, (state, action) => {
        state.fetchPictureStatus = 'failed';
        state.fetchPictureError = action.payload || action.error?.message || 'Failed to fetch display picture';
      });

    builder
      .addCase(upsertGroupDisplayPicture.pending, (state) => {
        state.upsertPictureStatus = 'loading';
        state.upsertPictureError = null;
      })
      .addCase(upsertGroupDisplayPicture.fulfilled, (state, action) => {
        state.upsertPictureStatus = 'succeeded';
        state.displayPicture = action.payload ?? state.displayPicture;
      })
      .addCase(upsertGroupDisplayPicture.rejected, (state, action) => {
        state.upsertPictureStatus = 'failed';
        state.upsertPictureError = action.payload || action.error?.message || 'Failed to upsert display picture';
      });

    builder
      .addCase(deleteGroupDisplayPicture.pending, (state) => {
        state.deletePictureStatus = 'loading';
        state.deletePictureError = null;
      })
      .addCase(deleteGroupDisplayPicture.fulfilled, (state) => {
        state.deletePictureStatus = 'succeeded';
        state.displayPicture = null;
      })
      .addCase(deleteGroupDisplayPicture.rejected, (state, action) => {
        state.deletePictureStatus = 'failed';
        state.deletePictureError = action.payload || action.error?.message || 'Failed to delete display picture';
      });

    // ===== SEARCH =====
    builder
      .addCase(searchUsers.pending, (state) => {
        state.searchStatus = 'loading';
        state.searchError = null;
        state.searchResults = [];
      })
      .addCase(searchUsers.fulfilled, (state, action) => {
        state.searchStatus = 'succeeded';
        const payload = action.payload;
        // Real response shape is UserSearchResponse ({users, total}) -- this
        // read `payload.results` (never a real field), so every search
        // silently returned zero results regardless of the query, all the
        // way back to whenever this reducer was written.
        state.searchResults = Array.isArray(payload?.users) ? payload.users : (Array.isArray(payload) ? payload : []);
      })
      .addCase(searchUsers.rejected, (state, action) => {
        state.searchStatus = 'failed';
        state.searchError = action.payload || action.error?.message || 'Failed to search users';
      });

    builder
      .addCase(searchGroupsByStock.pending, (state) => {
        state.searchStatus = 'loading';
        state.searchError = null;
        state.searchResults = [];
      })
      .addCase(searchGroupsByStock.fulfilled, (state, action) => {
        state.searchStatus = 'succeeded';
        const payload = action.payload;
        // Accept either { groups: [...] } or [...]
        state.searchResults = Array.isArray(payload?.groups) ? payload.groups : (Array.isArray(payload) ? payload : []);
      })
      .addCase(searchGroupsByStock.rejected, (state, action) => {
        state.searchStatus = 'failed';
        state.searchError = action.payload || action.error?.message || 'Failed to search groups by stock';
      });

    // ===== ANALYTICS =====
    builder
      .addCase(getGroupAnalytics.pending, (state) => {
        state.analyticsStatus = 'loading';
        state.analyticsError = null;
      })
      .addCase(getGroupAnalytics.fulfilled, (state, action) => {
        state.analyticsStatus = 'succeeded';
        state.analytics = action.payload ?? null;
      })
      .addCase(getGroupAnalytics.rejected, (state, action) => {
        state.analyticsStatus = 'failed';
        state.analyticsError = action.payload || action.error?.message || 'Failed to fetch analytics';
      });
  },
});

export const {
  clearCurrentGroup,
  clearSearchResults,
  resetAllErrors,
} = groupSlice.actions;

export default groupSlice.reducer;

// Optional selectors (handy)
export const selectGroups = (s) => s.groups.groups;
export const selectCurrentGroup = (s) => s.groups.currentGroup;
export const selectGroupMembers = (s) => s.groups.members;
export const selectGroupMessages = (s) => s.groups.messages;
export const selectGroupDisplayPicture = (s) => s.groups.displayPicture;
export const selectGroupSearchResults = (s) => s.groups.searchResults;
export const selectGroupAnalytics = (s) => s.groups.analytics;
export const selectMyGroupInvites = (s) => s.groups.myInvites;
export const selectFetchMyInvitesStatus = (s) => s.groups.fetchMyInvitesStatus;
